// react
import { ReactNode, useMemo, useState } from 'react';

// mui
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmationDialog } from '@/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';
import {
  CareCircle,
  ShortlistItem,
  ShortlistItemStatus,
} from '@karehero/models';
import { Icon } from '@/components/atoms/Icon';
import { StatusChip } from './StatusChip';

const InfoCard = ({ children }: { children: ReactNode }) => {
  return (
    <Card sx={{ backgroundColor: 'lightblue' }}>
      <Stack
        direction='row'
        spacing={2}
        sx={{
          p: 1,
          alignItems: 'center',
        }}
      >
        <Icon icon='info-circle' size='lg' />
        <Typography
          sx={{
            fontSize: 'small',
          }}
        >
          {children}
        </Typography>
      </Stack>
    </Card>
  );
};

const SelectableShortlist = ({ shortlist }: { shortlist: Shortlist }) => {
  return (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        alignItems: 'center',
      }}
    >
      <Typography variant='body2'>{shortlist.name}</Typography>
      {!shortlist.isPublished && <Chip label='draft' size='small' />}
      {shortlist.isPublished && (
        <Chip label='published' size='small' color='limeGreen' />
      )}
    </Stack>
  );
};

interface Shortlist {
  id: string;
  name: string;
  isPublished?: boolean;
  numProviders?: number;
}
interface ShortlistSelectProps {
  selectedShortlistID: string | null;
  onSelectedShortlistIDChange: (newID: string | null) => void;
  shortlists: Shortlist[];
  onEdit: (id: string) => void;
}
const ShortlistSelect = ({
  selectedShortlistID,
  onSelectedShortlistIDChange,
  shortlists,
  onEdit,
}: ShortlistSelectProps) => {
  const selectedShortlist = useMemo(
    () => shortlists.find((shortlist) => shortlist.id === selectedShortlistID),
    [selectedShortlistID, shortlists],
  );

  return (
    <FormControl fullWidth size='small'>
      <Select
        notched
        value={selectedShortlistID ?? ''}
        onChange={(e) => onSelectedShortlistIDChange(e.target.value || null)}
        label='Shortlist'
        renderValue={() =>
          selectedShortlist ? (
            <SelectableShortlist shortlist={selectedShortlist} />
          ) : (
            ''
          )
        }
      >
        {shortlists.map((shortlist) => (
          <MenuItem key={shortlist.id} value={shortlist.id}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <SelectableShortlist shortlist={shortlist} />
            </Box>
            {/* Editing lives on the row it belongs to. The click still selects
                and closes the menu, so the edit dialog opens over the page
                rather than behind an open menu. */}
            <IconButton
              aria-label={`edit ${shortlist.name}`}
              onClick={() => onEdit(shortlist.id)}
              edge='end'
            >
              <Icon icon='pen' type='fas' size='xs' />
            </IconButton>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ShortlistItemCard = ({
  item,
  onStatusChange,
  onDismiss,
  onNoteSave,
  onClick,
}: {
  item: ShortlistItem;
  onStatusChange?: (regulatoryId: string, status: ShortlistItemStatus) => void;
  onDismiss?: (regulatoryId: string) => void;
  onNoteSave?: (regulatoryId: string, note: string) => Promise<boolean> | void;
  onClick?: (regulatoryId: string) => void;
}) => {
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState(item.note ?? '');

  const handleSaveNote = async () => {
    const result = await onNoteSave?.(item.regulatoryId, noteDraft);
    if (result !== false) {
      setIsEditingNote(false);
    }
  };

  return (
    <Box onClick={() => onClick?.(item.regulatoryId)}>
      <Stack
        spacing={1}
        sx={{
          p: 1,
        }}
      >
        <Stack
          direction='row'
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='subtitle2' color='primary' noWrap>
            {item.provider?.name ?? 'Unknown provider'}
          </Typography>
          <Stack
            direction='row'
            spacing={0.5}
            sx={{
              alignItems: 'center',
            }}
          >
            {onStatusChange && onDismiss && (
              <StatusChip
                status={item.status}
                onStatusChange={(status) =>
                  onStatusChange(item.regulatoryId, status)
                }
                onDismiss={() => onDismiss(item.regulatoryId)}
              />
            )}
            {onNoteSave && (
              <IconButton
                color={item.note ? 'primary' : 'default'}
                size='small'
                aria-label={item.note ? 'Edit note' : 'Add note'}
                onClick={() => {
                  setNoteDraft(item.note ?? '');
                  setIsEditingNote(!isEditingNote);
                }}
              >
                <Icon icon='pen-field' size='xs' />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {isEditingNote ? (
          <Stack
            spacing={1}
            sx={{
              alignItems: 'end',
            }}
          >
            <TextField
              variant='filled'
              size='small'
              autoFocus
              fullWidth
              multiline
              minRows={2}
              placeholder='Add a note about this provider'
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
            />
            <div>
              <Button variant='contained' size='small' onClick={handleSaveNote}>
                Save
              </Button>
            </div>
          </Stack>
        ) : (
          item.note && (
            <Typography
              variant='body1'
              {...(onNoteSave && {
                onClick: () => {
                  setNoteDraft(item.note ?? '');
                  setIsEditingNote(true);
                },
                sx: { cursor: 'pointer' },
              })}
              sx={{
                color: 'text.secondary',
              }}
            >
              {item.note}
            </Typography>
          )
        )}
        <Divider sx={{ pt: 2 }} />
      </Stack>
    </Box>
  );
};

const DismissedItemCard = ({
  item,
  onRestore,
}: {
  item: ShortlistItem;
  onRestore?: (regulatoryId: string) => void;
}) => (
  <Box>
    <Stack
      spacing={1}
      sx={{
        p: 1,
        opacity: 0.7,
      }}
    >
      <Stack
        direction='row'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='subtitle2' color='primary' noWrap>
          {item.provider?.name ?? 'Unknown provider'}
        </Typography>
        <Stack
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Chip label='Dismissed' color='error' size='small' />
        </Stack>
      </Stack>
      {item.dismissReason && (
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
          }}
        >
          {item.dismissReason}
          {item.dismissDetail && `: ${item.dismissDetail}`}
        </Typography>
      )}
      {onRestore && (
        <Button
          size='small'
          variant='text'
          onClick={() => onRestore(item.regulatoryId)}
        >
          Restore
        </Button>
      )}
      <Divider sx={{ pt: 2 }} />
    </Stack>
  </Box>
);

const ShortlistItemList = ({
  items,
  onStatusChange,
  onDismiss,
  onRestore,
  onNoteSave,
  onItemClick,
}: {
  items: ShortlistItem[];
  onStatusChange?: (regulatoryId: string, status: ShortlistItemStatus) => void;
  onDismiss?: (regulatoryId: string) => void;
  onRestore?: (regulatoryId: string) => void;
  onNoteSave?: (regulatoryId: string, note: string) => Promise<boolean> | void;
  onItemClick?: (regulatoryId: string) => void;
}) => {
  const [showDismissed, setShowDismissed] = useState(false);

  const activeItems = useMemo(() => items.filter((i) => !i.dismissed), [items]);
  const dismissedItems = useMemo(
    () => items.filter((i) => i.dismissed),
    [items],
  );

  if (items.length === 0) {
    return (
      <Typography
        variant='body2'
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          py: 2,
        }}
      >
        No providers yet
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {activeItems.map((item) => (
        <ShortlistItemCard
          key={item.regulatoryId}
          item={item}
          onStatusChange={onStatusChange}
          onDismiss={onDismiss}
          onNoteSave={onNoteSave}
          onClick={onItemClick}
        />
      ))}
      {dismissedItems.length > 0 && (
        <>
          <Button
            size='small'
            variant='text'
            onClick={() => setShowDismissed(!showDismissed)}
          >
            {showDismissed ? 'Hide' : 'Show'} dismissed ({dismissedItems.length}
            )
          </Button>
          {showDismissed &&
            dismissedItems.map((item) => (
              <DismissedItemCard
                key={item.regulatoryId}
                item={item}
                onRestore={onRestore}
              />
            ))}
        </>
      )}
    </Stack>
  );
};

interface ShortlistsDrawerProps {
  onPublish: ({
    careCircleID,
    id,
  }: {
    careCircleID: string;
    id: string;
  }) => void;
  onEdit: (id: string) => void;
  onCreate: () => void;
  careCircle?: CareCircle;
  shortlists: Shortlist[];
  shortlistID: string | null;
  setShortlistID: (val: string | null) => void;
  items?: ShortlistItem[];
  onItemStatusChange?: (
    regulatoryId: string,
    status: ShortlistItemStatus,
  ) => void;
  onItemDismiss?: (regulatoryId: string) => void;
  onItemRestore?: (regulatoryId: string) => void;
  onItemNoteSave?: (
    regulatoryId: string,
    note: string,
  ) => Promise<boolean> | void;
  onItemClick?: (regulatoryId: string) => void;
}
export const ShortlistsDrawer = ({
  onPublish,
  onEdit,
  onCreate,
  careCircle,
  shortlists,
  shortlistID,
  setShortlistID,
  items = [],
  onItemStatusChange,
  onItemDismiss,
  onItemRestore,
  onItemNoteSave,
  onItemClick,
}: ShortlistsDrawerProps) => {
  const [isPublishingID, setIsPublishingID] = useState<string | null>(null);

  const shortlist = useMemo(
    () => shortlists.find((shortlist) => shortlist.id === shortlistID),
    [shortlists, shortlistID],
  );

  if (shortlists.length === 0) {
    return (
      <Stack
        spacing={2}
        sx={{
          px: 2,
        }}
      >
        <InfoCard>
          No shortlists have been added yet. Either press 'create shortlist' or
          add one from the care search to get started. You can create as many
          shortlists as you want, depending how varied the needs of the member
          are.
        </InfoCard>
        {/* Nothing to select yet, so creating has to live here too */}
        <Button variant='outlined' size='small' onClick={onCreate}>
          Create shortlist
        </Button>
      </Stack>
    );
  }

  return (
    // Column split in two: the shortlist and its providers scroll, Publish is
    // pinned to the bottom of the card so it's reachable at any scroll position.
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
      }}
    >
      <ConfirmationDialog
        isConfirming={isPublishingID !== null}
        onCancel={() => setIsPublishingID(null)}
        warningText={`'${shortlist?.name || 'Shortlist'}' has ${shortlist?.numProviders ?? 0} providers. Publishing makes it visible to ${careCircle?.careRecipientAccount?.fullName || ''}'s care-circle, notifies them, and adds a "Review your shortlist" action. You can keep editing after publishing.`}
        confirmationTitle='Publish Shortlist'
        confirmText='Publish shortlist'
        onConfirm={() => {
          isPublishingID &&
            careCircle &&
            onPublish({ careCircleID: careCircle.id, id: isPublishingID });
          setIsPublishingID(null);
        }}
      />
      <Stack
        spacing={2}
        sx={{
          px: 2,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <ShortlistSelect
                shortlists={shortlists}
                selectedShortlistID={shortlistID}
                onSelectedShortlistIDChange={setShortlistID}
                onEdit={onEdit}
              />
            </Box>
            <IconButton
              size='small'
              aria-label='create shortlist'
              onClick={onCreate}
            >
              <Icon icon='plus' fontSize={16} />
            </IconButton>
          </Stack>
          {shortlistID && !shortlist?.isPublished && (
            <InfoCard>Shortlist has unpublished changes.</InfoCard>
          )}
        </Stack>

        <ShortlistItemList
          items={items}
          onStatusChange={onItemStatusChange}
          onDismiss={onItemDismiss}
          onRestore={onItemRestore}
          onNoteSave={onItemNoteSave}
          onItemClick={onItemClick}
        />
      </Stack>
      <Box
        sx={{
          px: 2,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Button
          variant='contained'
          fullWidth
          disabled={!shortlistID || shortlist?.isPublished}
          onClick={() => setIsPublishingID(shortlistID)}
        >
          Publish
        </Button>
      </Box>
    </Stack>
  );
};

ShortlistsDrawer.displayName = 'ShortlistsDrawer';
export default ShortlistsDrawer;
